import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  TrendingUp,
  Users,
  Store,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Award,
  MapPin,
  RefreshCw,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  GET_DAILY_VISIT_TREND,
  GET_FSR_LEADERBOARD,
  GET_REPORT_SUMMARY,
  GET_STATUS_BREAKDOWN,
  GET_TERRITORY_BREAKDOWN,
} from '../graphql/queries';

// ── Date helpers ────────────────────────────────────────────────
const toIso = (d: Date) => d.toISOString().slice(0, 10);
const todayIso = () => toIso(new Date());
const subtractDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toIso(d);
};

const PRESETS: { id: string; label: string; days: number }[] = [
  { id: '7', label: 'Last 7 days', days: 6 },
  { id: '30', label: 'Last 30 days', days: 29 },
  { id: '90', label: 'Last 90 days', days: 89 },
];

const STATUS_COLORS: Record<string, string> = {
  done: '#10B981',
  pending: '#94A3B8',
  visiting: '#F59E0B',
  skipped: '#EF4444',
};

const STATUS_LABEL: Record<string, string> = {
  done: 'Completed',
  pending: 'Pending',
  visiting: 'In Progress',
  skipped: 'Skipped',
};

const initialsOf = (name?: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const fmtShortDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

// ── Page ────────────────────────────────────────────────────────
export const ReportsAnalytics: React.FC = () => {
  const [presetId, setPresetId] = useState<string>('30');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');

  const fromDate = useMemo(() => {
    if (presetId === 'custom') return customFrom || subtractDays(29);
    const p = PRESETS.find((x) => x.id === presetId);
    return subtractDays(p?.days ?? 29);
  }, [presetId, customFrom]);

  const toDate = useMemo(() => {
    if (presetId === 'custom') return customTo || todayIso();
    return todayIso();
  }, [presetId, customTo]);

  const variables = { fromDate, toDate };

  const summaryQuery = useQuery(GET_REPORT_SUMMARY, { variables, fetchPolicy: 'cache-and-network' });
  const trendQuery = useQuery(GET_DAILY_VISIT_TREND, { variables, fetchPolicy: 'cache-and-network' });
  const leaderboardQuery = useQuery(GET_FSR_LEADERBOARD, {
    variables: { ...variables, limit: 10 },
    fetchPolicy: 'cache-and-network',
  });
  const statusQuery = useQuery(GET_STATUS_BREAKDOWN, { variables, fetchPolicy: 'cache-and-network' });
  const territoryQuery = useQuery(GET_TERRITORY_BREAKDOWN, {
    variables: { ...variables, limit: 8 },
    fetchPolicy: 'cache-and-network',
  });

  const summary = summaryQuery.data?.getReportSummary ?? {
    totalVisits: 0,
    completed: 0,
    pending: 0,
    skipped: 0,
    completionRate: 0,
    uniqueRetailers: 0,
    activeFsrs: 0,
    registeredFsrs: 0,
  };
  const trend = trendQuery.data?.getDailyVisitTrend ?? [];
  const leaderboard = leaderboardQuery.data?.getFsrLeaderboard ?? [];
  const status = statusQuery.data?.getStatusBreakdown ?? [];
  const territory = territoryQuery.data?.getTerritoryBreakdown ?? [];

  const refresh = () => {
    summaryQuery.refetch();
    trendQuery.refetch();
    leaderboardQuery.refetch();
    statusQuery.refetch();
    territoryQuery.refetch();
  };

  const trendFormatted = useMemo(
    () => trend.map((d: any) => ({ ...d, label: fmtShortDate(d.date) })),
    [trend],
  );

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text">Reports & Analytics</h1>
          <p className="text-text2">Field operations across {fromDate} → {toDate}</p>
        </div>
        <button
          onClick={refresh}
          className="h-10 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2 flex items-center gap-2"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      {/* Date range picker */}
      <div className="card p-4 flex flex-wrap items-center gap-3">
        <Calendar size={16} className="text-primary" />
        <span className="text-xs font-bold uppercase tracking-widest text-text3">Range</span>
        <div className="flex gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPresetId(p.id)}
              className={`h-9 px-3 rounded-lg text-xs font-bold transition-colors
                ${presetId === p.id ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200 text-text2'}`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => setPresetId('custom')}
            className={`h-9 px-3 rounded-lg text-xs font-bold transition-colors
              ${presetId === 'custom' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200 text-text2'}`}
          >
            Custom
          </button>
        </div>
        {presetId === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              value={customFrom || subtractDays(29)}
              max={customTo || todayIso()}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-9 px-2 bg-gray-50 border border-border rounded-lg text-xs font-semibold focus:border-primary outline-none"
            />
            <span className="text-text3 text-xs">→</span>
            <input
              type="date"
              value={customTo || todayIso()}
              min={customFrom}
              max={todayIso()}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-9 px-2 bg-gray-50 border border-border rounded-lg text-xs font-semibold focus:border-primary outline-none"
            />
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Total Visits"
          value={summary.totalVisits}
          icon={Calendar}
          color="from-indigo-600 to-indigo-700"
        />
        <KpiCard
          label="Completed"
          value={summary.completed}
          subtext={`${summary.completionRate}% rate`}
          icon={CheckCircle2}
          color="from-emerald-600 to-emerald-700"
        />
        <KpiCard
          label="Active Field Officers"
          value={summary.activeFsrs}
          subtext={`of ${summary.registeredFsrs} registered`}
          icon={Users}
          color="from-blue-600 to-blue-700"
        />
        <KpiCard
          label="Retailers Covered"
          value={summary.uniqueRetailers}
          icon={Store}
          color="from-amber-600 to-amber-700"
        />
      </div>

      {/* Trend chart */}
      <div className="card p-6">
        <header className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-text flex items-center gap-2">
              <TrendingUp size={18} className="text-primary" /> Daily Visit Trend
            </h2>
            <p className="text-xs text-text3">Scheduled visits vs completed visits per day.</p>
          </div>
        </header>
        {trendQuery.loading && trend.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-text3 text-sm">Loading trend…</div>
        ) : trend.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-text3 text-sm">
            No visit data in this range.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trendFormatted} margin={{ top: 8, right: 12, bottom: 8, left: -8 }}>
              <defs>
                <linearGradient id="schedFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#4F46E5" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="doneFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} stroke="#CBD5E1" />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} stroke="#CBD5E1" allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #E2E8F0',
                  fontSize: 12,
                  boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="scheduled"
                name="Scheduled"
                stroke="#4F46E5"
                strokeWidth={2}
                fill="url(#schedFill)"
              />
              <Area
                type="monotone"
                dataKey="completed"
                name="Completed"
                stroke="#10B981"
                strokeWidth={2}
                fill="url(#doneFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leaderboard (col-span-2) */}
        <div className="card p-6 lg:col-span-2">
          <header className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-text flex items-center gap-2">
                <Award size={18} className="text-amber-500" /> FSR Leaderboard
              </h2>
              <p className="text-xs text-text3">Top officers by completed visits.</p>
            </div>
          </header>
          {leaderboardQuery.loading && leaderboard.length === 0 ? (
            <div className="py-12 text-center text-text3 text-sm">Loading leaderboard…</div>
          ) : leaderboard.length === 0 ? (
            <div className="py-12 text-center text-text3 text-sm">No FSR activity in this range.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-[10px] font-black text-text3 uppercase tracking-widest border-b border-border">
                  <tr>
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Officer</th>
                    <th className="py-2 pr-3 text-right">Visits</th>
                    <th className="py-2 pr-3 text-right">Completed</th>
                    <th className="py-2 text-right">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {leaderboard.map((row: any, idx: number) => (
                    <tr key={row.fsrId} className="hover:bg-gray-50/50">
                      <td className="py-3 pr-3 text-xs font-black text-text3 w-6">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs shadow-sm shrink-0"
                            style={{ backgroundColor: row.color || '#4F46E5' }}
                          >
                            {row.avatar || initialsOf(row.name)}
                          </div>
                          <div>
                            <div className="font-bold text-text">{row.name}</div>
                            <div className="text-[10px] text-text3 font-mono">{row.employeeId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-right font-semibold text-text">{row.totalVisits}</td>
                      <td className="py-3 pr-3 text-right font-bold text-emerald-600">{row.completed}</td>
                      <td className="py-3 text-right">
                        <div className="inline-flex flex-col items-end gap-1">
                          <span className="text-xs font-bold text-text">{row.completionRate}%</span>
                          <div className="w-20 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500"
                              style={{ width: `${row.completionRate}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Status breakdown (donut) */}
        <div className="card p-6">
          <header className="mb-4">
            <h2 className="text-lg font-bold text-text">Visit Status</h2>
            <p className="text-xs text-text3">Distribution across the window.</p>
          </header>
          {statusQuery.loading && status.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-text3 text-sm">Loading…</div>
          ) : status.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-text3 text-sm">No data.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={status}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {status.map((s: any) => (
                      <Cell key={s.status} fill={STATUS_COLORS[s.status] || '#94A3B8'} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: any, _n: any, p: any) => [v, STATUS_LABEL[p?.payload?.status] || p?.payload?.status]}
                    contentStyle={{
                      borderRadius: 12,
                      border: '1px solid #E2E8F0',
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {status.map((s: any) => (
                  <div key={s.status} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: STATUS_COLORS[s.status] || '#94A3B8' }}
                      />
                      <span className="text-text2 font-semibold">
                        {STATUS_LABEL[s.status] || s.status}
                      </span>
                    </div>
                    <span className="font-bold text-text">{s.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Territory breakdown */}
      <div className="card p-6">
        <header className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-text flex items-center gap-2">
              <MapPin size={18} className="text-rose-500" /> Top Territories
            </h2>
            <p className="text-xs text-text3">Cities by visit volume.</p>
          </div>
        </header>
        {territoryQuery.loading && territory.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-text3 text-sm">Loading…</div>
        ) : territory.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-text3 text-sm">
            No territory data — retailer city not set on visits.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, territory.length * 36)}>
            <BarChart
              data={territory}
              layout="vertical"
              margin={{ top: 8, right: 16, bottom: 8, left: 16 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="city"
                tick={{ fontSize: 11, fill: '#64748B' }}
                width={120}
                stroke="#CBD5E1"
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #E2E8F0',
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="totalVisits" name="Total" fill="#4F46E5" radius={[0, 6, 6, 0]} />
              <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

// ── KPI card ────────────────────────────────────────────────────
const KpiCard: React.FC<{
  label: string;
  value: number;
  subtext?: string;
  icon: any;
  color: string;
}> = ({ label, value, subtext, icon: Icon, color }) => (
  <div className="bg-white p-5 rounded-3xl border border-white shadow-sm flex items-center gap-4">
    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-lg shrink-0`}>
      <Icon size={20} strokeWidth={2.5} />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{label}</p>
      <div className="text-2xl font-black text-slate-900 tracking-tight leading-none">{value.toLocaleString('en-IN')}</div>
      {subtext && <p className="text-[10px] text-text3 mt-1 font-semibold">{subtext}</p>}
    </div>
  </div>
);
